import type { HtmlAttributes } from '@studio/design-system/props';
import type { Settings } from '@studio/models/control';
import { Protocol } from '@studio/models/control';
import type { Configuration } from '@studio/models/control/visca-serial';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { resolveProtocolSettings } from './normalize.js';
import { ViscaSerial as _ViscaSerial } from '#~/control/components/editor/index.js';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  const settings = useStore(
    ({ candidate }) =>
      // We know this is a normalized VISCA/Serial configuration.
      candidate.control as Configuration,
    shallow
  );

  const handleChange = useLatestFn((value: Settings) => {
    producer((draft) => {
      draft.candidate.control = value;
    });
  });

  return <_ViscaSerial settings={toAccessor(settings, handleChange)} {...props} />;
};

/** Visca/Serial settings. */
export const ViscaSerial = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const [isPrimed, setPrimed] = useState(false);
  const producer = useProducer();

  // On mount, normalize the Visca/Serial control settings.
  useMount(() => {
    queueMicrotask(() => {
      producer((draft) => {
        const {
          candidate,
          manufacturer: { config }
        } = draft;

        const { control } = candidate;

        // Moved verbatim to normalize.ts so the first-run funnel resolves the
        // same endpoint without rendering this panel. See that file's header.
        draft.candidate.control = resolveProtocolSettings(
          Protocol.VISCA_SERIAL,
          control,
          config,
          undefined
        ) as Settings<Configuration>;
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
